import { mapKeys } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'

import { captureInvalidProductStepUpdate } from 'embed/constants/errors'

import {
  applicationStepSelected,
  enableCCHybridOfferList,
  fetchOffer,
  fetchRateTableFromState,
  filtersPopulated,
  flowSelected,
  setMaxResults,
  setRateTableUuid,
  stepSelected,
  updateConfigStep,
} from 'common/actions'
import Parent from 'common/services/Parent'
import QueryParams from 'common/services/QueryParams'
import * as actions from 'embed/actions'

import { ApplicationStep, FlowQueryParamForProduct, isUuid, ProductFlow } from 'embed/constants'

import { usePrevious } from 'embed/hooks'

import {
  APPLICATION_FIELD_QUERY_PARAM_PREFIX,
  ClientTags,
  Config,
  ConfigKeys,
  ConfigServiceDataKeys,
  CreditCardStep,
  DepositsFlow,
  DepositsStep,
  Filter,
  FILTER_QUERY_PARAM_PREFIX,
  Filters,
  LifeInsuranceStep,
  LoanFlow,
  LoanStep,
  MortgageStep,
  Product,
  ProductStep,
  PublishKey,
  QueryParamKeys,
} from 'even-ts-static'

import {
  configApplicationFlowSelector,
  configAvailableProductsSelector,
  configProductSelector,
  configStepSelector,
  depositsFlowSelector,
} from 'embed/selectors/config'
import { CCIntroVsPreviewVsHybridOfferList, withExperiment } from 'embed/experiments'
import { MultiProductState } from 'embed/reducers'
import { SelectedStepState } from 'embed/reducers/ui'
import { uiSelectedStepSelector, uiViewportWidthSelector } from 'embed/selectors/ui'
import App from './index'

interface DispatchProps {
  dispatchApplicationStepSelected: (product: Product, applicationStep: ApplicationStep) => void
  dispatchAvailableProducts: (products: Product[]) => void
  dispatchFetchOffer: (product: Product, offerUuid: string) => void
  dispatchFetchRateTableFromState: (product: Product) => void
  dispatchFiltersPopulated: (product: Product, filters: Filters) => void
  dispatchFlowSelected: (product: Product, flow: ProductFlow) => void
  dispatchProductSelected: (product: Product) => void
  dispatchSetMaxResults: (product: Product, max: number) => void
  dispatchSetRateTableUuid: (product: Product, rateTableUuid: string) => void
  dispatchStepSelected: (product: Product, step: ProductStep) => void
  dispatchUpdateConfigStep: (step: ProductStep) => void
  dispatchEnableCCHybridOfferList?: () => void
}

interface StoreProps {
  applicationFlow?: LoanFlow
  availableProductsFromConfig?: Product[]
  clientTags?: ClientTags
  config?: Config
  configProduct?: Product
  configSelectedStep?: ProductStep
  depositsFlow?: DepositsFlow
  filterParams?: Filters
  maxResults?: number
  offerUuid?: string
  partnerKey?: string
  plaidModalOpen: boolean
  resultsUuid?: string
  selectedProduct?: Product
  selectedStep?: SelectedStepState
  selectedVariantId?: string
  viewportWidth?: number
}

type Props = StoreProps & DispatchProps

export const AppContainer: React.FC<Props> = (props) => {
  const { selectedProduct: product } = props
  const step: SelectedStepState | undefined = product && props.selectedStep?.[product]
  const previousStep = usePrevious(step)

  // eslint-disable-next-line complexity
  React.useEffect(() => {
    const { availableProductsFromConfig = [] } = props

    // Get the list of product from the query params otherwise use our defaults
    const availableProducts = availableProductsFromConfig.length > 0 ? availableProductsFromConfig : [Product.Loan]

    props.dispatchAvailableProducts(availableProducts)

    const selectedProduct = props.selectedProduct || props.configProduct || availableProducts[0]

    if (selectedProduct === Product.CreditCard) {
      if (!!props.selectedVariantId && props.configSelectedStep !== CreditCardStep.Results) {
        switch (props.selectedVariantId) {
          case CCIntroVsPreviewVsHybridOfferList.variants.HybridOfferList.id:
            props.dispatchEnableCCHybridOfferList?.()
            props.dispatchUpdateConfigStep(CreditCardStep.Preview)
            props.dispatchStepSelected(selectedProduct, CreditCardStep.Preview)
            break
          case CCIntroVsPreviewVsHybridOfferList.variants.Preview.id:
            props.dispatchUpdateConfigStep(CreditCardStep.Preview)
            props.dispatchStepSelected(selectedProduct, CreditCardStep.Preview)
            break
          case CCIntroVsPreviewVsHybridOfferList.variants.IntroStep.id:
          default:
            props.dispatchUpdateConfigStep(CreditCardStep.Intro)
            props.dispatchStepSelected(selectedProduct, CreditCardStep.Intro)
        }
      } else if (props.configSelectedStep !== undefined) {
        props.dispatchStepSelected(selectedProduct, props.configSelectedStep)
      } else {
        props.dispatchProductSelected(selectedProduct)
      }
    } else {
      if (props.configSelectedStep !== undefined) {
        props.dispatchStepSelected(selectedProduct, props.configSelectedStep)
      } else {
        props.dispatchProductSelected(selectedProduct)
      }
    }

    const selectedFlow = props?.config?.[FlowQueryParamForProduct[selectedProduct]]
    if (selectedFlow !== undefined) {
      props.dispatchFlowSelected(selectedProduct, selectedFlow)
    }

    // Load a specific offer or a specific rate table
    if (!!props.offerUuid) {
      props.dispatchFetchOffer(selectedProduct, props.offerUuid)
    }
    if (props.resultsUuid !== undefined && (!props.offerUuid || selectedProduct === Product.LifeInsurance)) {
      props.dispatchSetRateTableUuid(selectedProduct, props.resultsUuid)
      props.dispatchFetchRateTableFromState(selectedProduct)
    }

    // Set filters from query params
    if (props.filterParams !== undefined) {
      props.dispatchFiltersPopulated(selectedProduct, props.filterParams)
    }

    // Set maximum results
    if (props.maxResults !== undefined) {
      props.dispatchSetMaxResults(selectedProduct, props.maxResults)
    }

    // Send client tags to the parent
    if (props.clientTags !== undefined && Object.keys(props.clientTags).length > 0) {
      Parent.publishMessage(PublishKey.ClientTagsLoaded, props.clientTags)
    }
  }, [])

  React.useEffect(() => {
    if (step && previousStep && step !== previousStep) {
      // When switching between steps (within a given product)
      // Scroll the parent page to the top of the iframe
      let stepEnum
      switch (product) {
        case Product.Deposits:
          stepEnum = DepositsStep
          break
        case Product.CreditCard:
          stepEnum = CreditCardStep
          break
        case Product.Mortgage:
          stepEnum = MortgageStep
          break
        case Product.LifeInsurance:
          stepEnum = LifeInsuranceStep
          break
        default:
          stepEnum = LoanStep
      }
      const possibleSteps = Object.values(stepEnum)
      if (possibleSteps.includes(step)) {
        Parent.scrollToFrame()
      } else {
        captureInvalidProductStepUpdate(step)
      }
    }
  })

  return <App {...props} />
}

export const mapStateToProps = (state: MultiProductState, ownProps): StoreProps => {
  // Look for query params that begin with FILTER_QUERY_PARAM_PREFIX and map them to remove the FILTER_QUERY_PARAM_PREFIX
  // e.g. {filter.zipcode: '43220'} becomes {zipcode: '43220'}
  let filterParams = Object.keys(state.config).reduce((acc, key) => {
    if (key.startsWith(FILTER_QUERY_PARAM_PREFIX) || key.startsWith(APPLICATION_FIELD_QUERY_PARAM_PREFIX)) {
      acc[key] = state.config[key]
    }

    return acc
  }, {}) as Filters

  filterParams = mapKeys(filterParams, (value, key) =>
    key.startsWith(FILTER_QUERY_PARAM_PREFIX)
      ? key.slice(FILTER_QUERY_PARAM_PREFIX.length)
      : key.slice(APPLICATION_FIELD_QUERY_PARAM_PREFIX.length),
  )

  const applicationFlow: LoanFlow | undefined = configApplicationFlowSelector(state)
  const availableProductsFromConfig: Product[] | undefined = configAvailableProductsSelector(state)
  const configProduct: Product | undefined = configProductSelector(state)
  const configSelectedStep: ProductStep | undefined = configStepSelector(state)
  const depositsFlow: DepositsFlow | undefined = depositsFlowSelector(state)
  const filterBlacklist = [Filter.ConsentsToFCRA, Filter.ConsentsToTCPA, Filter.SocialSecurityNumber]
  const selectedStep = uiSelectedStepSelector(state)
  // If the zipcode is not present in query params, filter it out here because we only want
  // the zipcode that comes from the config to be a fallback if geoip fails.
  const zipcodeInQueryParam = QueryParams.getAll()[FILTER_QUERY_PARAM_PREFIX]?.[QueryParamKeys.Zipcode] !== undefined
  if (!zipcodeInQueryParam) {
    filterBlacklist.push(Filter.Zipcode)
  }

  filterParams = Object.keys(filterParams).reduce((acc, key: Filter) => {
    if (!filterBlacklist.includes(key)) {
      acc[key] = filterParams[key]
    }
    return acc
  }, {})
  const selectedVariantId = ownProps.selectedVariantId

  const offerUuid = isUuid(state.config[ConfigKeys.Offer] ?? '') ? state.config[ConfigKeys.Offer] : undefined

  return {
    applicationFlow,
    availableProductsFromConfig,
    clientTags: state.config[ConfigKeys.clientTags],
    config: state.config,
    configProduct,
    configSelectedStep,
    depositsFlow,
    filterParams,
    maxResults: state.config[QueryParamKeys.MaxResults],
    offerUuid,
    partnerKey: state.config[ConfigServiceDataKeys.partner],
    plaidModalOpen: state.ui.plaid.isModalOpen,
    resultsUuid: state.config[ConfigKeys.ResultsUuid],
    selectedProduct: state.ui.selectedProduct,
    selectedStep,
    selectedVariantId,
    viewportWidth: uiViewportWidthSelector(state),
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchApplicationStepSelected: (product: Product, applicationStep: ApplicationStep) =>
    dispatch(applicationStepSelected(product, applicationStep)),
  dispatchAvailableProducts: (products: Product[]) => dispatch(actions.setAvailableProducts(products)),
  dispatchFetchOffer: (product: Product, offerUuid: string) => dispatch(fetchOffer(product, offerUuid)),
  dispatchFetchRateTableFromState: (product: Product) => dispatch(fetchRateTableFromState(product)),
  dispatchFiltersPopulated: (product: Product, filters: Filters) => dispatch(filtersPopulated(product, filters)),
  dispatchFlowSelected: (product: Product, flow: ProductFlow) => dispatch(flowSelected(product, flow)),
  dispatchProductSelected: (product: Product) => dispatch(actions.productSelected(product)),
  dispatchSetMaxResults: (product: Product, max: number) => dispatch(setMaxResults(product, max)),
  dispatchSetRateTableUuid: (product: Product, rateTableUuid: string) =>
    dispatch(setRateTableUuid(product, rateTableUuid)),
  dispatchStepSelected: (product: Product, step: ProductStep) => dispatch(stepSelected(product, step)),
  dispatchUpdateConfigStep: (step: ProductStep) => dispatch(updateConfigStep(step)),
  dispatchEnableCCHybridOfferList: () => dispatch(enableCCHybridOfferList()),
})

const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(AppContainer)

export default withExperiment(CCIntroVsPreviewVsHybridOfferList)(ConnectedApp)
